import type { Extension, Schedule } from "@medplum/fhirtypes";
import { asHealthcareServiceId, type HealthcareServiceId } from "@repo/domain";
import { UCUM_SYSTEM } from "@repo/fhir-systems";
import { HEALTHCARE_SERVICE_ENTITY_ID_SYSTEM } from "@repo/fhir-systems/identifiers";
import { SCHEDULING_PARAMS_URL } from "../lib/activity-definition-codec";

/** Local definition — mirrors ScheduleModel["schedulingParameters"][number] */
export interface SchedulingParameters {
  service: { resourceType: "HealthcareService"; id: HealthcareServiceId };
  duration: { value: number; unit: "min" | "h" };
  timezone?: string;
  availability?: {
    dayOfWeek: Array<"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun">;
    startTime: string;
    endTime: string;
  };
  bufferBefore?: { value: number; unit: "min" | "h" };
  bufferAfter?: { value: number; unit: "min" | "h" };
  alignmentInterval?: { value: number; unit: "min" | "h" };
  alignmentOffset?: { value: number; unit: "min" | "h" };
  bookingAdvanceLimit?: { value: number; unit: "d" | "wk" };
  bookingLimit?: Array<{ maxPerPeriod: number; periodUnit: "d" | "wk" }>;
}

/**
 * Resolve scheduling params for a specific HealthcareService.
 * Each SchedulingParameters entry on a Schedule is keyed by its `service` ref —
 * pick the entry whose service.id matches the requested healthcareServiceId,
 * otherwise return the first entry (caller's chosen default).
 */
export function resolveSchedulingParams(
  params: SchedulingParameters[] | undefined,
  healthcareServiceId?: HealthcareServiceId,
): SchedulingParameters | undefined {
  if (!params?.length) return undefined;
  if (healthcareServiceId) {
    const match = params.find((p) => p.service.id === healthcareServiceId);
    if (match) return match;
  }
  return params[0];
}

// ── FHIR ↔ Domain serialization ──

function toFhirDuration(value: number, unit: string): { value: number; unit: string; system: string; code: string } {
  return { value, unit, system: UCUM_SYSTEM, code: unit };
}

/** Coerce HH:MM or HH:MM:SS → HH:MM:SS (FHIR `time` type). */
function toTimeOfDay(t: string): string {
  return t.length === 5 ? `${t}:00` : t;
}

export function schedulingParamsToExtension(
  params: SchedulingParameters,
  serviceReference: string,
): {
  url: string;
  extension: Array<Record<string, unknown>>;
} {
  const subExtensions: Array<Record<string, unknown>> = [];

  // Required: service reference identifies which HealthcareService these params apply to.
  subExtensions.push({
    url: "service",
    valueReference: {
      reference: serviceReference,
      identifier: { system: HEALTHCARE_SERVICE_ENTITY_ID_SYSTEM, value: params.service.id },
    },
  });
  subExtensions.push({ url: "duration", valueDuration: toFhirDuration(params.duration.value, params.duration.unit) });
  if (params.timezone) {
    subExtensions.push({ url: "timezone", valueCode: params.timezone });
  }
  if (params.availability) {
    const startTimeHHMMSS = toTimeOfDay(params.availability.startTime);
    const endTimeHHMMSS = toTimeOfDay(params.availability.endTime);
    const availableTimeExt: Array<Record<string, unknown>> = [
      ...params.availability.dayOfWeek.map((day) => ({ url: "daysOfWeek", valueCode: day })),
      { url: "availableStartTime", valueTime: startTimeHHMMSS },
      { url: "availableEndTime", valueTime: endTimeHHMMSS },
    ];
    subExtensions.push({
      url: "availability",
      extension: [{ url: "availableTime", extension: availableTimeExt }],
    });
  }
  if (params.bufferBefore) {
    subExtensions.push({
      url: "bufferBefore",
      valueDuration: toFhirDuration(params.bufferBefore.value, params.bufferBefore.unit),
    });
  }
  if (params.bufferAfter) {
    subExtensions.push({
      url: "bufferAfter",
      valueDuration: toFhirDuration(params.bufferAfter.value, params.bufferAfter.unit),
    });
  }
  if (params.alignmentInterval) {
    subExtensions.push({
      url: "alignmentInterval",
      valueDuration: toFhirDuration(params.alignmentInterval.value, params.alignmentInterval.unit),
    });
  }
  if (params.alignmentOffset) {
    subExtensions.push({
      url: "alignmentOffset",
      valueDuration: toFhirDuration(params.alignmentOffset.value, params.alignmentOffset.unit),
    });
  }
  if (params.bookingAdvanceLimit) {
    subExtensions.push({
      url: "bookingAdvanceLimit",
      valueDuration: toFhirDuration(params.bookingAdvanceLimit.value, params.bookingAdvanceLimit.unit),
    });
  }
  if (params.bookingLimit?.length) {
    for (const limit of params.bookingLimit) {
      subExtensions.push({
        url: "bookingLimit",
        extension: [
          { url: "maxPerPeriod", valueInteger: limit.maxPerPeriod },
          { url: "periodUnit", valueCode: limit.periodUnit },
        ],
      });
    }
  }

  return { url: SCHEDULING_PARAMS_URL, extension: subExtensions };
}

function parseSchedulingParam(ext: { extension?: Array<Record<string, unknown>> }): SchedulingParameters | null {
  const nested = ext.extension ?? [];
  const durationExt = nested.find((e) => e.url === "duration") as
    | { valueDuration?: { value?: number; code?: string } }
    | undefined;
  const durationValue = durationExt?.valueDuration?.value;
  const durationUnit = durationExt?.valueDuration?.code;
  if (!durationValue || !durationUnit) return null;

  const timezoneExt = nested.find((e) => e.url === "timezone") as { valueCode?: string } | undefined;
  const serviceExt = nested.find((e) => e.url === "service") as
    | { valueReference?: { reference?: string; identifier?: { system?: string; value?: string } } }
    | undefined;
  const serviceId =
    serviceExt?.valueReference?.identifier?.system === HEALTHCARE_SERVICE_ENTITY_ID_SYSTEM
      ? serviceExt.valueReference.identifier.value
      : undefined;
  if (!serviceId) return null;

  const availabilityExt = nested.find((e) => e.url === "availability") as
    | { extension?: Array<{ url?: string; extension?: Array<Record<string, unknown>> }> }
    | undefined;
  const availableTime = availabilityExt?.extension?.find((e) => e.url === "availableTime");
  const availableTimeSubs = (availableTime?.extension ?? []) as Array<Record<string, unknown>>;
  const dayOfWeek = availableTimeSubs
    .filter((e) => e.url === "daysOfWeek")
    .map((e) => (e as { valueCode?: string }).valueCode)
    .filter((v): v is string => !!v);
  const startTimeRaw = (availableTimeSubs.find((e) => e.url === "availableStartTime") as { valueTime?: string })
    ?.valueTime;
  const endTimeRaw = (availableTimeSubs.find((e) => e.url === "availableEndTime") as { valueTime?: string })?.valueTime;
  const startTime = startTimeRaw?.slice(0, 5);
  const endTime = endTimeRaw?.slice(0, 5);

  // Duration-valued sub-extensions
  const parseDuration = (url: string) => {
    const ext = nested.find((e) => e.url === url) as { valueDuration?: { value?: number; code?: string } } | undefined;
    if (!ext?.valueDuration?.value || !ext.valueDuration.code) return undefined;
    return { value: ext.valueDuration.value, unit: ext.valueDuration.code as "min" | "h" };
  };

  const bufferBefore = parseDuration("bufferBefore");
  const bufferAfter = parseDuration("bufferAfter");
  const alignmentInterval = parseDuration("alignmentInterval");
  const alignmentOffset = parseDuration("alignmentOffset");

  const bookingAdvanceLimitExt = nested.find((e) => e.url === "bookingAdvanceLimit") as
    | { valueDuration?: { value?: number; code?: string } }
    | undefined;
  const bookingAdvanceLimit =
    bookingAdvanceLimitExt?.valueDuration?.value && bookingAdvanceLimitExt.valueDuration.code
      ? {
          value: bookingAdvanceLimitExt.valueDuration.value,
          unit: bookingAdvanceLimitExt.valueDuration.code as "d" | "wk",
        }
      : undefined;

  const bookingLimitExts = nested.filter((e) => e.url === "bookingLimit") as Array<{
    extension?: Array<Record<string, unknown>>;
  }>;
  const bookingLimit = bookingLimitExts
    .map((ext) => {
      const maxExt = ext.extension?.find((e) => e.url === "maxPerPeriod") as { valueInteger?: number } | undefined;
      const unitExt = ext.extension?.find((e) => e.url === "periodUnit") as { valueCode?: string } | undefined;
      if (!maxExt?.valueInteger || !unitExt?.valueCode) return null;
      return { maxPerPeriod: maxExt.valueInteger, periodUnit: unitExt.valueCode as "d" | "wk" };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);

  return {
    service: { resourceType: "HealthcareService", id: asHealthcareServiceId(serviceId) },
    duration: { value: durationValue, unit: durationUnit as "min" | "h" },
    ...(timezoneExt?.valueCode ? { timezone: timezoneExt.valueCode } : {}),
    ...(dayOfWeek.length && startTime && endTime
      ? {
          availability: {
            dayOfWeek: dayOfWeek as NonNullable<SchedulingParameters["availability"]>["dayOfWeek"],
            startTime,
            endTime,
          },
        }
      : {}),
    ...(bufferBefore ? { bufferBefore } : {}),
    ...(bufferAfter ? { bufferAfter } : {}),
    ...(alignmentInterval ? { alignmentInterval } : {}),
    ...(alignmentOffset ? { alignmentOffset } : {}),
    ...(bookingAdvanceLimit ? { bookingAdvanceLimit } : {}),
    ...(bookingLimit.length ? { bookingLimit } : {}),
  };
}

export function parseSchedulingParameters(extension: Extension[] | undefined): SchedulingParameters[] | undefined {
  const params = (extension ?? [])
    .filter((ext) => ext.url === SCHEDULING_PARAMS_URL)
    .map((ext) => parseSchedulingParam(ext as { extension?: Array<Record<string, unknown>> }))
    .filter((v): v is SchedulingParameters => v !== null);
  return params.length ? params : undefined;
}

export function extractScheduleTimezone(schedule: Schedule): string {
  const params = parseSchedulingParameters(schedule.extension);
  return params?.[0]?.timezone ?? "UTC";
}
